/**
 * Phase A3 — `/set-password` shared component.
 *
 * Two contexts:
 *   ?context=invite  — employee just clicked an invitation link; we have
 *                      a laguna_employee_portal_session cookie. POSTs to
 *                      /api/employee/password/set, then navigates to
 *                      ?next=... (the wizard).
 *   ?context=quote   — employer just submitted a quote; we have a
 *                      laguna_proposal_session cookie. POSTs to
 *                      /api/proposal/password/set, then stays on the
 *                      proposal portal (or navigates to ?next=...).
 *
 * Anyone reaching this page without a session will get 401 on submit; we
 * surface a "session expired" copy + send them back to the right entry.
 *
 * Strength + breach feedback uses A1's POST /api/auth/password/validate
 * (HIBP k-anonymity). Debounced 300ms after typing pauses; gated until
 * length ≥ 8 to avoid HIBP spam. Breach is a HARD submit block.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
    setEmployeePassword,
    setEmployerPassword,
    setProposalPassword,
    validatePassword,
    passwordState,
} from "../lib/api";
import { track } from "../lib/analytics";
import BackLink from "../components/BackLink";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import "./SetPassword.css";

const VALIDATE_DEBOUNCE_MS = 300;
const MIN_LEN = 8;

function isInternalNext(next) {
    return typeof next === "string" && next.startsWith("/") && !next.startsWith("//");
}

export default function SetPassword() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const rawContext = params.get("context");
    // Phase A5 — `forced` = admin-minted temp pw, unskippable.
    const context = rawContext === "quote"
        ? "quote"
        : rawContext === "forced" ? "forced" : "invite";
    const nextUrl = params.get("next");

    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState("");
    // Validation outcome from /api/auth/password/validate (debounced).
    const [validation, setValidation] = useState({
        valid: false, breached: false, length_ok: false, checking: false,
    });
    // Gate state — undefined = checking, "ok" = render form, "redirect" = navigated away
    const [gate, setGate] = useState(undefined);
    // Phase A5 — for context=forced we need to know which role endpoint
    // (employer vs employee) to POST /password/set against. passwordState()
    // tells us via its `context` response; stash it here.
    const [gateRole, setGateRole] = useState(null);
    const debounceRef = useRef(null);
    const pwInputRef = useRef(null);

    // Phase A3 — guard: anyone hitting /set-password who already has a
    // password (or who isn't signed in) gets bounced. Password change is
    // A4's scope; this surface is strictly first-time setup.
    //
    // Phase A5 — `context=forced` bypasses the has-password check because
    // the admin just minted a temp password; the user MUST replace it.
    useEffect(() => {
        let mounted = true;
        passwordState().then(
            (res) => {
                if (!mounted) return;
                if (res.has_password && context !== "forced") {
                    track("set_password_gate_redirect", {
                        reason: "already_has_password", context,
                    });
                    const dest = isInternalNext(nextUrl)
                        ? nextUrl
                        : context === "quote" ? "/" : "/portal";
                    window.location.replace(dest);
                    setGate("redirect");
                } else {
                    setGate("ok");
                    setGateRole(res.context);
                }
            },
            (err) => {
                // 401 → no session → bounce to the right entry surface.
                track("set_password_gate_redirect", {
                    reason: "no_session", context,
                    status: err?.response?.status,
                });
                window.location.replace(
                    context === "quote" ? "/employers" : "/apply",
                );
                setGate("redirect");
            },
        );
        return () => { mounted = false; };
        // Run once on mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        track("password_setup_view", { context });
        document.title = t(`auth.setPassword.${context}Heading`);
    }, [context, t]);

    // Focus password input only after gate clears.
    useEffect(() => {
        if (gate === "ok" && pwInputRef.current) {
            pwInputRef.current.focus();
        }
    }, [gate]);

    /* --- Inline validation (length + HIBP via debounced API) --- */
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (pw1.length === 0) {
            setValidation({ valid: false, breached: false, length_ok: false, checking: false });
            return;
        }
        if (pw1.length < MIN_LEN) {
            setValidation({ valid: false, breached: false, length_ok: false, checking: false });
            return;
        }
        // length_ok locally, defer HIBP to debounce
        setValidation((v) => ({ ...v, length_ok: true, checking: true }));
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await validatePassword(pw1);
                setValidation({ ...res, checking: false });
            } catch (_) {
                // Network blip → fail-open on the validate side too. The
                // server-side /password/set still runs HIBP, so we don't
                // accidentally let a breached password through.
                setValidation({
                    valid: true, breached: false, length_ok: true, checking: false,
                });
            }
        }, VALIDATE_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [pw1]);

    const matchState = useMemo(() => {
        if (pw1.length === 0 && pw2.length === 0) return "empty";
        if (pw2.length === 0) return "empty";
        if (pw1 === pw2) return "match";
        return "mismatch";
    }, [pw1, pw2]);

    const canSubmit =
        !submitting &&
        validation.length_ok &&
        !validation.breached &&
        !validation.checking &&
        matchState === "match";

    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitErr("");
        setSubmitting(true);
        try {
            if (context === "forced") {
                // Phase A5 — after admin-minted temp pw. The session is
                // already live (employer_session or employee_portal_session);
                // route by the context passwordState returned for us.
                if (gateRole === "employer") {
                    await setEmployerPassword(pw1);
                } else {
                    await setEmployeePassword(pw1);
                }
                track("password_set_ok", { context });
                const dest = isInternalNext(nextUrl)
                    ? nextUrl
                    : gateRole === "employer" ? "/portal" : "/portal";
                window.location.replace(dest);
            } else if (context === "invite") {
                await setEmployeePassword(pw1);
                track("password_set_ok", { context });
                const dest = isInternalNext(nextUrl) ? nextUrl : "/apply/form";
                window.location.replace(dest);
            } else {
                await setProposalPassword(pw1);
                track("password_set_ok", { context });
                const dest = isInternalNext(nextUrl) ? nextUrl : "/";
                // Quote-context users stay on their proposal portal; if
                // no next was provided we fall back to the home page.
                window.location.replace(dest);
            }
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            if (status === 400 && detail === "password_breached") {
                setValidation((v) => ({ ...v, breached: true, valid: false }));
                setSubmitErr("breached");
            } else if (status === 400 && detail === "password_too_short") {
                setSubmitErr("tooShort");
            } else if (status === 401) {
                setSubmitErr("sessionExpired");
                track("password_set_session_expired", { context });
            } else {
                setSubmitErr("network");
            }
            track("password_set_error", { context, status, detail });
        } finally {
            setSubmitting(false);
        }
    }

    const eyebrowKey = context === "quote"
        ? "auth.setPassword.quoteEyebrow"
        : context === "forced"
            ? "auth.setPassword.forcedEyebrow"
            : "auth.setPassword.inviteEyebrow";
    const headingKey = context === "quote"
        ? "auth.setPassword.quoteHeading"
        : context === "forced"
            ? "auth.setPassword.forcedHeading"
            : "auth.setPassword.inviteHeading";
    const subheadKey = context === "forced"
        ? "auth.setPassword.forcedSubhead"
        : "auth.setPassword.subhead";
    // Phase A5 — forced context is unskippable; hide the back link.
    const backTo = context === "forced"
        ? null
        : context === "quote" ? "/employers" : "/employees";

    /* --- Inline feedback row --- */
    const feedbackKey = (() => {
        if (validation.breached) return "auth.setPassword.feedback.breached";
        if (pw1.length > 0 && !validation.length_ok) {
            return "auth.setPassword.feedback.tooShort";
        }
        if (validation.length_ok && !validation.checking) {
            return "auth.setPassword.feedback.longEnough";
        }
        return null;
    })();
    const feedbackTone = validation.breached
        ? "warn"
        : validation.length_ok ? "ok" : "muted";

    /* --- Submit-time error banner copy --- */
    const submitErrKey = {
        breached: "auth.setPassword.feedback.breached",
        tooShort: "auth.setPassword.feedback.tooShort",
        sessionExpired: "auth.setPassword.errors.sessionExpired",
        network: "auth.setPassword.errors.network",
    }[submitErr];

    return (
        <section className="set-password" data-testid="set-password-page">
            {backTo && (
                <div className="set-password-backrow">
                    <BackLink to={backTo} testId="set-password-back" />
                </div>
            )}

            {gate === undefined && (
                <div
                    className="set-password-inner"
                    data-testid="set-password-loading"
                    aria-busy="true"
                >
                    <p className="set-password-eyebrow mono">{t(eyebrowKey)}</p>
                </div>
            )}
            {gate === "ok" && (
            <div className="set-password-inner">
                <p className="set-password-eyebrow mono">{t(eyebrowKey)}</p>
                <h1 className="set-password-title">{t(headingKey)}</h1>
                <p className="set-password-sub">{t(subheadKey)}</p>

                {submitErrKey && (
                    <div
                        className="set-password-error-banner"
                        role="status"
                        data-testid="set-password-error-banner"
                    >
                        {t(submitErrKey)}
                    </div>
                )}

                <form
                    className="set-password-form"
                    onSubmit={handleSubmit}
                    autoComplete="on"
                    data-testid="set-password-form"
                    noValidate
                >
                    <label className="set-password-label mono" htmlFor="set-password-new">
                        {t("auth.setPassword.newPasswordLabel")}
                    </label>
                    <input
                        ref={pwInputRef}
                        id="set-password-new"
                        type="password"
                        name="new-password"
                        autoComplete="new-password"
                        minLength={MIN_LEN}
                        required
                        value={pw1}
                        onChange={(e) => setPw1(e.target.value)}
                        onBlur={() => {
                            // Cancel the debounce and call straight away so
                            // paste-and-tab doesn't leave the user without
                            // a HIBP signal.
                            if (debounceRef.current) clearTimeout(debounceRef.current);
                            if (pw1.length >= MIN_LEN) {
                                setValidation((v) => ({ ...v, checking: true }));
                                validatePassword(pw1).then(
                                    (res) => setValidation({ ...res, checking: false }),
                                    () => setValidation((v) => ({ ...v, checking: false })),
                                );
                            }
                        }}
                        className="set-password-input"
                        data-testid="set-password-new"
                    />
                    <div
                        className={`set-password-feedback set-password-feedback-${feedbackTone}`}
                        aria-live="polite"
                        data-testid="set-password-feedback"
                    >
                        {feedbackKey ? t(feedbackKey, { min: MIN_LEN }) : "\u00A0"}
                    </div>

                    <label className="set-password-label mono" htmlFor="set-password-confirm">
                        {t("auth.setPassword.confirmPasswordLabel")}
                    </label>
                    <input
                        id="set-password-confirm"
                        type="password"
                        name="confirm-password"
                        autoComplete="new-password"
                        required
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        className="set-password-input"
                        data-testid="set-password-confirm"
                    />
                    <div
                        className={`set-password-feedback set-password-feedback-${
                            matchState === "match" ? "ok"
                                : matchState === "mismatch" ? "warn" : "muted"
                        }`}
                        aria-live="polite"
                        data-testid="set-password-match"
                    >
                        {matchState === "mismatch"
                            ? t("auth.setPassword.feedback.mismatched")
                            : matchState === "match"
                                ? t("auth.setPassword.feedback.matched")
                                : "\u00A0"}
                    </div>

                    <button
                        type="submit"
                        className="set-password-submit"
                        disabled={!canSubmit}
                        data-testid="set-password-submit"
                    >
                        {submitting
                            ? t("auth.setPassword.submitting")
                            : t("auth.setPassword.submit")}
                    </button>
                </form>

                <div className="set-password-secondary" data-testid="set-password-secondary">
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="set-password-secondary-link"
                        data-testid="set-password-contact"
                    >
                        {t("auth.signin.errors.contactUs")}
                    </a>
                </div>
            </div>
            )}
        </section>
    );
}
